/**
 * @fileoverview Whether a photo is too small for its frame to fill without
 * enlarging it (B32).
 *
 * A frame that fills (object-cover) blows a small picture up: a 120 pixel
 * cut-out from a seed-order photo comes out blurry on a plant card. A photo
 * that would need enlarging is instead drawn at its own size, centred on the
 * frame's own colour (owner, 2026-10-20). coverPlacement is the same rule for the
 * share pictures' canvas drawing (canvasKit drawCover). refitFocus (B86) keeps a
 * gardener's crop when the frame's shape changes. Pure, so it is tested without a page.
 *
 * Where: FramedPhoto (shared/components/ui), the plant card, tile and row.
 * API: none.
 *
 * @module photoFit
 */

/** A width and a height in pixels. */
export interface PixelSize {
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
}

/**
 * Where a crop sits in its frame: x and y (0..1 across and down), the zoom (1 = just
 * filling the frame) and, once the gardener has cropped, the width over height of the
 * slot it was made in (a), which lets it be refitted to another shape (refitFocus).
 */
export interface CropFocus {
  /** Across, 0..1. */
  x: number;
  /** Down, 0..1. */
  y: number;
  /** Zoom, 1 = just filling the frame. */
  z?: number;
  /** The width over the height of the slot the crop was made in; none for a crop never set. */
  a?: number;
}

/**
 * The part of a photograph, as shares of its width and height, that a slot shows
 * when it is just filled (zoom 1).
 *
 * @param photo - The photograph's width over its height.
 * @param slot - The slot's width over its height.
 * @returns The shares across and down.
 */
function fillWindow(photo: number, slot: number): { w: number; h: number } {
  return slot >= photo ? { w: 1, h: photo / slot } : { w: slot / photo, h: 1 };
}

/**
 * A crop made in one slot shape, fitted to another (B86): the same centre, and the
 * zoom lowered only as far as the new shape needs for what was framed to stay in
 * view (never raised, never below filling the slot). The centre is held inside the
 * photograph. The original crop is kept by the caller and refitted each time, so
 * going back to the first shape gives the first crop back.
 *
 * @param focus - The crop, with the shape it was made in (a); without a, or with the same shape, it is returned as it is.
 * @param photo - The photograph's own size.
 * @param slot - The new slot's width over its height.
 * @returns The crop for the new slot, recording its shape.
 */
export function refitFocus(focus: CropFocus, photo: PixelSize, slot: number): CropFocus {
  const from = focus.a;
  if (!from || !(slot > 0) || photo.width <= 0 || photo.height <= 0 || Math.abs(from - slot) < 1e-3) return focus;
  const p = photo.width / photo.height;
  const z = Math.max(1, focus.z ?? 1);
  const was = fillWindow(p, from);
  const ww = was.w / z;
  const wh = was.h / z;
  const cx = (1 - ww) * focus.x + ww / 2;
  const cy = (1 - wh) * focus.y + wh / 2;
  const now = fillWindow(p, slot);
  const nz = Math.max(1, Math.min(z, now.w / ww, now.h / wh));
  const nw = now.w / nz;
  const nh = now.h / nz;
  /** The crop's place (0..1) that puts a window of this size round this centre, held inside the photograph. */
  const place = (centre: number, win: number) => (1 - win < 1e-9 ? 0.5 : Math.min(1, Math.max(0, (centre - win / 2) / (1 - win))));
  return { x: place(cx, nw), y: place(cy, nh), z: nz, a: slot };
}

/**
 * True when filling the frame with this picture would enlarge it, that is,
 * either side of the picture is shorter than the frame's. An unknown size
 * (not loaded yet, or a frame not laid out) is never "smaller", so the
 * picture is drawn as it always was.
 *
 * @param natural - The picture's own pixel size.
 * @param frame - The frame's size in pixels.
 * @returns Whether the picture should be drawn at its own size.
 */
export function isSmallerThanFrame(natural: PixelSize, frame: PixelSize): boolean {
  if (natural.width <= 0 || natural.height <= 0 || frame.width <= 0 || frame.height <= 0) return false;
  return natural.width < frame.width || natural.height < frame.height;
}

/**
 * Where and how large to draw a picture in a rectangle (what the share
 * pictures' canvas drawing uses): filling it, cropped where focus says and
 * zoomed as asked, unless the picture is too small to fill it, in which case
 * it is drawn at its own size in the middle, never enlarged (shrunk only if it
 * still cannot fit).
 *
 * @param image - The picture's own size.
 * @param rect - The rectangle's size.
 * @param focus - Which part the crop keeps, 0..1 across and down, and the zoom (1 = just filling); a crop made in another slot shape (a) is refitted to this one first (refitFocus).
 * @returns The drawn size and the offset of its top left corner within the rectangle.
 */
export function coverPlacement(
  image: PixelSize, rect: PixelSize, crop: CropFocus = { x: 0.5, y: 0.5 },
): { width: number; height: number; dx: number; dy: number } {
  const focus = rect.height > 0 ? refitFocus(crop, image, rect.width / rect.height) : crop;
  const fill = Math.max(rect.width / image.width, rect.height / image.height);
  const small = fill > 1;
  const scale = small
    ? Math.min(1, rect.width / image.width, rect.height / image.height)
    : fill * Math.max(1, focus.z ?? 1);
  const width = image.width * scale;
  const height = image.height * scale;
  const fx = small ? 0.5 : Math.min(1, Math.max(0, focus.x));
  const fy = small ? 0.5 : Math.min(1, Math.max(0, focus.y));
  return { width, height, dx: (rect.width - width) * fx, dy: (rect.height - height) * fy };
}
